import { z } from 'zod';

/**
 * Tolerant scalar parsers for the ASIC-backed API.
 *
 * The backend is not consistent about JSON types. The same field arrives as a
 * number on one endpoint and a quoted string on another (`"329.00"`), and
 * booleans arrive as real booleans on some records and as `"true"`/`"false"` on
 * others. The legacy client hid this by coercing at every call site behind
 * `any`-typed responses; its TypeScript definitions say `number` and `boolean`
 * and are simply wrong.
 *
 * This matters more than it looks. Zod rejects the WHOLE response when one
 * field mismatches, so a single quoted figure turns a 200 OK into "couldn't
 * load this company" on screen. Being permissive on the way in and strict on
 * the way out is what keeps a backend type change from blanking a screen.
 *
 * Use these for values the feed controls. Keep `z.string()` and `z.number()`
 * for values this app itself produces, where a wrong type is a real bug worth
 * failing on.
 */

/** A value the feed may send as a string or a number, kept as a string.
 *  For identifiers and display values — never for arithmetic. */
export const looseString = z
  .union([z.string(), z.number()])
  .transform(String)
  .nullish()
  .transform((value) => value ?? '');

/**
 * A boolean the feed may send as a real boolean, a string, or 0/1.
 *
 * `Boolean('false')` is `true` — every non-empty string is truthy — so the
 * string case must be COMPARED, not coerced. Getting this wrong is silent: it
 * inverts a flag rather than throwing, which is how every registered address
 * came to be labelled a business address.
 */
export const looseBoolean = z
  .union([z.string(), z.boolean(), z.number()])
  .nullish()
  .transform((value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    return typeof value === 'string' && value.toLowerCase() === 'true';
  });

/** A number the feed may quote. Money and counts both arrive this way. */
export const looseNumber = z.coerce.number().catch(0).default(0);
